import React, { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Film, 
  Upload, 
  Download, 
  Image as ImageIcon, 
  X, 
  ZoomIn, 
  ArrowLeft, 
  Sliders,
  Plus,
  Minus,
  Shuffle,
  ChevronDown,
  ChevronUp,
  Settings,
  Info
} from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Types
interface Frame {
  id: string;
  blob: Blob;
  url: string;
  timestamp: number;
}

interface ExtractionOptions {
  method: 'specific' | 'sequential' | 'shuffle';
  fps: number;
  specificFrames: number[];
  shuffleCount: number;
  framesPerSecond: number; // New option for frames per second
}

interface AdvancedSettings {
  fileFormat: 'jpg' | 'png';
  quality: number;
  filenamePattern: string;
  maxWidth: number | null;
  maxHeight: number | null;
  maintainAspectRatio: boolean;
}

function App() {
  // State
  const [video, setVideo] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [frames, setFrames] = useState<Frame[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [selectedFrame, setSelectedFrame] = useState<Frame | null>(null);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [options, setOptions] = useState<ExtractionOptions>({
    method: 'sequential',
    fps: 1,
    specificFrames: [1, 5, 10],
    shuffleCount: 10,
    framesPerSecond: 3 // Default to 3 frames per second
  });
  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettings>({
    fileFormat: 'jpg',
    quality: 90,
    filenamePattern: 'frame-{timestamp}',
    maxWidth: null,
    maxHeight: null,
    maintainAspectRatio: true
  });
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Dropzone setup
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type.startsWith('video/')) {
      setVideo(file);
      setVideoUrl(URL.createObjectURL(file));
      setFrames([]);
      setSelectedFrame(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': []
    },
    maxFiles: 1
  });

  // Extract frames from video
  const extractFrames = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !video) return;
    
    setIsExtracting(true);
    setFrames([]);
    setExtractionProgress(0);
    
    const videoElement = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;
    
    // Set canvas dimensions to match video or respect max dimensions
    let canvasWidth = videoElement.videoWidth;
    let canvasHeight = videoElement.videoHeight;
    
    if (advancedSettings.maxWidth && advancedSettings.maxHeight) {
      if (advancedSettings.maintainAspectRatio) {
        const widthRatio = advancedSettings.maxWidth / canvasWidth;
        const heightRatio = advancedSettings.maxHeight / canvasHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        
        canvasWidth = Math.floor(canvasWidth * ratio);
        canvasHeight = Math.floor(canvasHeight * ratio);
      } else {
        canvasWidth = advancedSettings.maxWidth;
        canvasHeight = advancedSettings.maxHeight;
      }
    } else if (advancedSettings.maxWidth) {
      if (advancedSettings.maintainAspectRatio) {
        const ratio = advancedSettings.maxWidth / canvasWidth;
        canvasWidth = advancedSettings.maxWidth;
        canvasHeight = Math.floor(canvasHeight * ratio);
      } else {
        canvasWidth = advancedSettings.maxWidth;
      }
    } else if (advancedSettings.maxHeight) {
      if (advancedSettings.maintainAspectRatio) {
        const ratio = advancedSettings.maxHeight / canvasHeight;
        canvasHeight = advancedSettings.maxHeight;
        canvasWidth = Math.floor(canvasWidth * ratio);
      } else {
        canvasHeight = advancedSettings.maxHeight;
      }
    }
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    const duration = videoElement.duration;
    const extractedFrames: Frame[] = [];
    
    if (options.method === 'sequential') {
      // New approach: Extract a specific number of frames per second
      const framesPerSecond = options.framesPerSecond;
      const frameInterval = 1 / framesPerSecond; // Time between frames in seconds
      const totalSeconds = Math.floor(duration);
      const totalFramesToExtract = totalSeconds * framesPerSecond;
      
      let frameCount = 0;
      
      for (let second = 0; second < totalSeconds; second++) {
        for (let frameInSecond = 0; frameInSecond < framesPerSecond; frameInSecond++) {
          const time = second + (frameInSecond * frameInterval);
          
          if (time >= duration) break; // Don't go past the video duration
          
          videoElement.currentTime = time;
          
          // Wait for the video to update its current time
          await new Promise<void>(resolve => {
            const onSeeked = () => {
              videoElement.removeEventListener('seeked', onSeeked);
              resolve();
            };
            videoElement.addEventListener('seeked', onSeeked);
          });
          
          // Draw the current frame on the canvas
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to blob
          const mimeType = `image/${advancedSettings.fileFormat}`;
          const quality = advancedSettings.fileFormat === 'png' ? undefined : advancedSettings.quality / 100;
          
          const blob = await new Promise<Blob>(resolve => {
            canvas.toBlob(blob => resolve(blob!), mimeType, quality);
          });
          
          // Create a URL for the blob
          const url = URL.createObjectURL(blob);
          
          // Add frame to the list
          extractedFrames.push({
            id: `frame-${frameCount}`,
            blob,
            url,
            timestamp: time
          });
          
          frameCount++;
          
          // Update progress
          setExtractionProgress(Math.round((frameCount / totalFramesToExtract) * 100));
        }
      }
    } else if (options.method === 'specific') {
      // Extract specific frame numbers (assuming 30fps video)
      const totalFrames = options.specificFrames.length;
      
      for (let i = 0; i < totalFrames; i++) {
        const frameNumber = options.specificFrames[i];
        const time = frameNumber / 30; // Assuming 30fps video
        
        videoElement.currentTime = time;
        
        // Wait for the video to update its current time
        await new Promise<void>(resolve => {
          const onSeeked = () => {
            videoElement.removeEventListener('seeked', onSeeked);
            resolve();
          };
          videoElement.addEventListener('seeked', onSeeked);
        });
        
        // Draw the current frame on the canvas
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to blob
        const mimeType = `image/${advancedSettings.fileFormat}`;
        const quality = advancedSettings.fileFormat === 'png' ? undefined : advancedSettings.quality / 100;
        
        const blob = await new Promise<Blob>(resolve => {
          canvas.toBlob(blob => resolve(blob!), mimeType, quality);
        });
        
        // Create a URL for the blob
        const url = URL.createObjectURL(blob);
        
        // Add frame to the list
        extractedFrames.push({
          id: `frame-${frameNumber}`,
          blob,
          url,
          timestamp: time
        });
        
        // Update progress
        setExtractionProgress(Math.round((i + 1) / totalFrames * 100));
      }
    } else if (options.method === 'shuffle') {
      // Extract random frames from the video
      const totalFrames = options.shuffleCount;
      const maxFrameNumber = Math.floor(duration * 30); // Assuming 30fps
      
      // Generate random frame numbers
      const randomFrames = new Set<number>();
      while (randomFrames.size < totalFrames) {
        const randomFrame = Math.floor(Math.random() * maxFrameNumber);
        randomFrames.add(randomFrame);
      }
      
      const randomFramesArray = Array.from(randomFrames);
      
      for (let i = 0; i < randomFramesArray.length; i++) {
        const frameNumber = randomFramesArray[i];
        const time = frameNumber / 30; // Assuming 30fps video
        
        videoElement.currentTime = time;
        
        // Wait for the video to update its current time
        await new Promise<void>(resolve => {
          const onSeeked = () => {
            videoElement.removeEventListener('seeked', onSeeked);
            resolve();
          };
          videoElement.addEventListener('seeked', onSeeked);
        });
        
        // Draw the current frame on the canvas
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to blob
        const mimeType = `image/${advancedSettings.fileFormat}`;
        const quality = advancedSettings.fileFormat === 'png' ? undefined : advancedSettings.quality / 100;
        
        const blob = await new Promise<Blob>(resolve => {
          canvas.toBlob(blob => resolve(blob!), mimeType, quality);
        });
        
        // Create a URL for the blob
        const url = URL.createObjectURL(blob);
        
        // Add frame to the list
        extractedFrames.push({
          id: `frame-${frameNumber}`,
          blob,
          url,
          timestamp: time
        });
        
        // Update progress
        setExtractionProgress(Math.round((i + 1) / randomFramesArray.length * 100));
      }
    }
    
    setFrames(extractedFrames);
    setIsExtracting(false);
  }, [video, options, advancedSettings]);

  // Generate filename based on pattern
  const generateFilename = (frame: Frame) => {
    let filename = advancedSettings.filenamePattern;
    filename = filename.replace('{timestamp}', Math.round(frame.timestamp * 1000).toString());
    return `${filename}.${advancedSettings.fileFormat}`;
  };

  // Download a single frame
  const downloadFrame = (frame: Frame) => {
    saveAs(frame.blob, generateFilename(frame));
  };

  // Download all frames
  const downloadAllFrames = async () => {
    const zip = new JSZip();
    
    frames.forEach((frame) => {
      zip.file(generateFilename(frame), frame.blob);
    });
    
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `extractify-frames-${new Date().getTime()}.zip`);
  };

  // Handle option changes
  const handleOptionChange = (key: keyof ExtractionOptions, value: any) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  // Handle advanced settings changes
  const handleAdvancedSettingChange = (key: keyof AdvancedSettings, value: any) => {
    setAdvancedSettings(prev => ({ ...prev, [key]: value }));
  };

  // Handle specific frames input
  const handleSpecificFramesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const framesStr = e.target.value;
    const framesArr = framesStr.split(',').map(f => parseInt(f.trim())).filter(f => !isNaN(f));
    handleOptionChange('specificFrames', framesArr);
  };

  // Handle clicking on Home link
  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideo(null);
    setVideoUrl(null);
    setFrames([]);
    setSelectedFrame(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-3 flex items-center">
          <div className="flex items-center space-x-2">
            <Film className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-800">Extractify</h1>
          </div>
          <div className="flex-1 flex items-center justify-center space-x-8">
            <a href="#" className="text-sm text-gray-600 hover:text-gray-900" onClick={handleHomeClick}>Home</a>
            <a href="#" className="text-sm text-gray-600 hover:text-gray-900" onClick={(e) => {
              e.preventDefault();
              setShowAboutModal(true);
            }}>About</a>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded-md">
            Feel Free To Use
          </button>
        </div>
      </header>

      {/* About Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button 
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              onClick={() => setShowAboutModal(false)}
            >
              <X size={20} />
            </button>
            
            <div className="flex flex-col items-center mb-4">
              <img src="/logo.png" alt="Root9" className="h-12 mb-4" />
              <h2 className="text-xl font-bold text-gray-800">About Extractify</h2>
              <p className="text-sm text-gray-600 mt-1">A product of Root9 Solutions</p>
            </div>
            
            <p className="text-gray-700 mb-4">
              Root9 Solutions is a forward-thinking technology company dedicated to delivering innovative solutions that meet the evolving needs of businesses. We specialize in tools that enhance efficiency and enable digital transformation.
            </p>
            
            <div className="flex justify-center space-x-4 text-sm text-blue-600">
              <a href="#" className="hover:underline">Website</a>
              <a href="#" className="hover:underline">Contact</a>
              <a href="#" className="hover:underline">Privacy Policy</a>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      {!video && !frames.length && (
        <div className="bg-white py-12 border-b border-gray-200">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Video Frame Extractor</h1>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              Extract frames from your videos with precision. Choose specific frames, extract sequentially by FPS,
              or get random frames with the shuffle option.
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 container mx-auto px-4 py-8 flex flex-col md:flex-row gap-6">
        {/* Options Panel */}
        <div className="w-full md:w-80 bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-fit">
          <h2 className="text-lg font-semibold mb-6">Extraction Options</h2>
          
          {/* Method Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extraction Method
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={options.method}
              onChange={(e) => handleOptionChange('method', e.target.value)}
            >
              <option value="sequential">Sequential (by FPS)</option>
              <option value="specific">Specific Frame Numbers</option>
              <option value="shuffle">Random Shuffle</option>
            </select>
          </div>
          
          {/* Method-specific options */}
          {options.method === 'sequential' ? (
            <>
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Frames Per Second
                  </label>
                  <span className="text-sm font-medium text-blue-600">{options.framesPerSecond}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => handleOptionChange('framesPerSecond', Math.max(1, options.framesPerSecond - 1))}
                    disabled={options.framesPerSecond <= 1}
                  >
                    <Minus size={16} />
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    value={options.framesPerSecond}
                    onChange={(e) => handleOptionChange('framesPerSecond', parseInt(e.target.value))}
                  />
                  <button 
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => handleOptionChange('framesPerSecond', Math.min(10, options.framesPerSecond + 1))}
                    disabled={options.framesPerSecond >= 10}
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Number of frames to extract per second of video
                </p>
              </div>
            </>
          ) : options.method === 'specific' ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Frame Numbers
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 1, 5, 10, 15"
                value={options.specificFrames.join(', ')}
                onChange={handleSpecificFramesChange}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter frame numbers separated by commas (based on 30fps)
              </p>
            </div>
          ) : (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Number of Random Frames
                </label>
                <span className="text-sm font-medium text-blue-600">{options.shuffleCount}</span>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => handleOptionChange('shuffleCount', Math.max(1, options.shuffleCount - 1))}
                  disabled={options.shuffleCount <= 1}
                >
                  <Minus size={16} />
                </button>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  value={options.shuffleCount}
                  onChange={(e) => handleOptionChange('shuffleCount', parseInt(e.target.value))}
                />
                <button 
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => handleOptionChange('shuffleCount', Math.min(50, options.shuffleCount + 1))}
                  disabled={options.shuffleCount >= 50}
                >
                  <Plus size={16} />
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Extracts random frames from throughout the video
              </p>
            </div>
          )}
          
          {/* Advanced Settings Toggle */}
          <div className="mb-6">
            <button 
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            >
              <Settings className="h-4 w-4 mr-1" />
              Advanced Settings
              {showAdvancedSettings ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              )}
            </button>
            
            {/* Advanced Settings Panel */}
            {showAdvancedSettings && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                {/* File Format */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Format
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={advancedSettings.fileFormat}
                    onChange={(e) => handleAdvancedSettingChange('fileFormat', e.target.value)}
                  >
                    <option value="jpg">JPEG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>
                
                {/* Quality (for JPG) */}
                {advancedSettings.fileFormat !== 'png' && (
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-sm font-medium text-gray-700">
                        Quality
                      </label>
                      <span className="text-sm font-medium text-blue-600">{advancedSettings.quality}%</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="1"
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      value={advancedSettings.quality}
                      onChange={(e) => handleAdvancedSettingChange('quality', parseInt(e.target.value))}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Lower quality</span>
                      <span>Higher quality</span>
                    </div>
                  </div>
                )}
                
                {/* Filename Pattern */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filename Pattern
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={advancedSettings.filenamePattern}
                    onChange={(e) => handleAdvancedSettingChange('filenamePattern', e.target.value)}
                    placeholder="frame-{timestamp}"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use {'{timestamp}'} as placeholder for frame time in ms
                  </p>
                </div>
                
                {/* Resize Options */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resize Options
                  </label>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Width</label>
                      <input
                        type="number"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={advancedSettings.maxWidth || ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseInt(e.target.value);
                          handleAdvancedSettingChange('maxWidth', value);
                        }}
                        placeholder="Original"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Height</label>
                      <input
                        type="number"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={advancedSettings.maxHeight || ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseInt(e.target.value);
                          handleAdvancedSettingChange('maxHeight', value);
                        }}
                        placeholder="Original"
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="maintainAspectRatio"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={advancedSettings.maintainAspectRatio}
                      onChange={(e) => handleAdvancedSettingChange('maintainAspectRatio', e.target.checked)}
                    />
                    <label htmlFor="maintainAspectRatio" className="ml-2 block text-sm text-gray-700">
                      Maintain aspect ratio
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Extract Button */}
          <div className="mb-4">
            <button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={extractFrames}
              disabled={!video || isExtracting}
            >
              {isExtracting ? (
                <>
                  <Sliders className="h-5 w-5 mr-2 animate-pulse" />
                  Extracting... {extractionProgress}%
                </>
              ) : (
                <>
                  <ImageIcon className="h-5 w-5 mr-2" />
                  Extract Frames
                </>
              )}
            </button>
          </div>
          
          {/* Reset Button */}
          {video && (
            <button
              className="w-full border border-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md hover:bg-gray-50"
              onClick={() => {
                if (videoUrl) URL.revokeObjectURL(videoUrl);
                setVideo(null);
                setVideoUrl(null);
                setFrames([]);
                setSelectedFrame(null);
              }}
            >
              Reset
            </button>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          {selectedFrame ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <button
                  className="flex items-center text-gray-600 hover:text-gray-900"
                  onClick={() => setSelectedFrame(null)}
                >
                  <ArrowLeft className="h-5 w-5 mr-1" />
                  Back to all frames
                </button>
                <div className="ml-auto">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-3 rounded-md flex items-center"
                    onClick={() => downloadFrame(selectedFrame)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center bg-black rounded-lg overflow-hidden p-4 relative">
                <img
                  src={selectedFrame.url}
                  alt={`Frame at ${selectedFrame.timestamp.toFixed(2)}s`}
                  className="max-w-full max-h-[70vh] object-contain"
                />
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full">
                  {selectedFrame.timestamp.toFixed(2)}s
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Frame at {selectedFrame.timestamp.toFixed(2)} seconds
                </div>
                <div className="text-sm text-gray-600">
                  {generateFilename(selectedFrame)}
                </div>
              </div>
            </div>
          ) : !video ? (
            <div
              {...getRootProps()}
              className={`bg-white rounded-lg shadow-sm border-2 border-dashed p-12 flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-16 w-16 text-blue-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-1">
                Drag & drop a video file here
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Or click to browse your files
              </p>
              <p className="text-xs text-gray-400 max-w-md text-center">
                Supported formats: MP4, WebM, MOV, AVI, etc.
              </p>
            </div>
          ) : frames.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-800">{video.name}</h3>
                  <p className="text-sm text-gray-500">
                    {(video.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    if (videoUrl) URL.revokeObjectURL(videoUrl);
                    setVideo(null);
                    setVideoUrl(null);
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center">
                {videoUrl && (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    className="max-w-full max-h-[60vh]"
                  />
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="mt-4 text-center text-sm text-gray-600">
                {isExtracting ? (
                  <div className="flex flex-col items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{ width: `${extractionProgress}%` }}
                      ></div>
                    </div>
                    <p>Extracting frames... {extractionProgress}%</p>
                  </div>
                ) : (
                  <p>Configure extraction options and click "Extract Frames"</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">
                  {frames.length} Files
                </h2>
                <div className="flex gap-2">
                  <button
                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-1.5 px-3 rounded-md flex items-center border border-gray-300"
                    onClick={downloadAllFrames}
                  >
                    <span className="mr-1">📁</span> Download as ZIP
                  </button>
                  <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-3 rounded-md flex items-center"
                    onClick={downloadAllFrames}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {frames.map((frame) => (
                  <div
                    key={frame.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col"
                  >
                    <div
                      className="h-36 bg-black flex items-center justify-center cursor-pointer relative group"
                      onClick={() => setSelectedFrame(frame)}
                    >
                      <img
                        src={frame.url}
                        alt={`Frame at ${frame.timestamp.toFixed(2)}s`}
                        className="max-w-full max-h-full object-contain"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="h-8 w-8 text-white" />
                      </div>
                      <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-2 py-0.5 rounded">
                        {frame.timestamp.toFixed(2)}s
                      </div>
                    </div>
                    <div className="p-2 flex items-center justify-between">
                      <div className="overflow-hidden">
                        <div className="text-xs font-medium text-gray-800 truncate">
                          {generateFilename(frame)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(frame.blob.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <button
                        className="text-gray-500 hover:text-blue-600 bg-gray-100 hover:bg-gray-200 p-1.5 rounded-full flex-shrink-0 ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadFrame(frame);
                        }}
                        title="Download frame"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;